/**
 * Phase 4 plan 04-06 — Settings → LLM Keys tab (LLM-02).
 *
 * Lists org LLM API keys grouped by provider; each row renders alias, health
 * badge, default indicator + set-default button, TestKeyButton, and delete.
 * Header button opens CreateLlmKeyDialog.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { AlertCircle, Key, Plus, Star, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { useOrganizationStore } from '@/stores/organization-store'
import { useLlmApiKeysStore } from '@/stores/llm-api-keys-store'
import { LLM_PROVIDER_INFO, type LLMAPIKey } from '@/schemas/llm-api-key'
import { ApiKeyHealthBadge } from './api-key-health-badge'
import { TestKeyButton } from './test-key-button'
import { CreateLlmKeyDialog } from './create-llm-key-dialog'

function groupByProvider(keys: LLMAPIKey[]): Record<string, LLMAPIKey[]> {
  const grouped: Record<string, LLMAPIKey[]> = {}
  for (const key of keys) {
    if (!grouped[key.provider]) grouped[key.provider] = []
    grouped[key.provider].push(key)
  }
  // Default key first within each provider
  for (const provider of Object.keys(grouped)) {
    grouped[provider].sort((a, b) => {
      if (a.is_default === b.is_default) return 0
      return a.is_default ? -1 : 1
    })
  }
  return grouped
}

export function LlmKeysTab() {
  const { t } = useTranslation()
  const { currentOrganization } = useOrganizationStore()
  const keys = useLlmApiKeysStore((s) => s.keys)
  const isLoading = useLlmApiKeysStore((s) => s.isLoading)
  const error = useLlmApiKeysStore((s) => s.error)
  const fetch = useLlmApiKeysStore((s) => s.fetch)
  const setDefault = useLlmApiKeysStore((s) => s.setDefault)
  const remove = useLlmApiKeysStore((s) => s.remove)

  const [createOpen, setCreateOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<LLMAPIKey | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (currentOrganization) void fetch(currentOrganization.id)
  }, [currentOrganization, fetch])

  if (!currentOrganization) {
    return (
      <Card variant="glass">
        <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-8 text-center">
          <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
          <p className="text-amber-400">{t('settings.llmKeys.noOrg')}</p>
        </div>
      </Card>
    )
  }

  const handleSetDefault = async (key: LLMAPIKey) => {
    try {
      await setDefault(currentOrganization.id, key.id)
      toast.success(
        t('settings.llmKeys.setDefaultSuccess', { name: key.name }),
      )
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : t('settings.llmKeys.setDefaultFailed'),
      )
    }
  }

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return
    setIsDeleting(true)
    try {
      await remove(currentOrganization.id, deleteTarget.id)
      toast.success(t('settings.llmKeys.deleteSuccess'))
      setDeleteTarget(null)
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : t('settings.llmKeys.deleteFailed'),
      )
    } finally {
      setIsDeleting(false)
    }
  }

  const grouped = groupByProvider(keys)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-100">
            {t('settings.llmKeys.title')}
          </h3>
          <p className="text-sm text-gray-400 mt-1">
            {t('settings.llmKeys.description')}
          </p>
        </div>
        <Button variant="action-indigo" onClick={() => setCreateOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t('settings.llmKeys.createButton')}
        </Button>
      </div>

      {error && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {isLoading && keys.length === 0 ? (
        <Card variant="glass" className="p-8 text-center">
          <p className="text-gray-400">{t('settings.llmKeys.loading')}</p>
        </Card>
      ) : keys.length === 0 ? (
        <Card variant="glass">
          <div className="p-8 text-center">
            <Key className="w-12 h-12 text-gray-500 mx-auto mb-3" />
            <p className="text-gray-400">{t('settings.llmKeys.empty')}</p>
            <Button
              variant="action-indigo"
              className="mt-4"
              onClick={() => setCreateOpen(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('settings.llmKeys.createButton')}
            </Button>
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([provider, providerKeys]) => (
            <div key={provider} className="space-y-3">
              <h4 className="text-sm font-semibold text-gray-300">
                {LLM_PROVIDER_INFO[
                  provider as keyof typeof LLM_PROVIDER_INFO
                ]?.label ?? provider}
              </h4>
              <div className="space-y-3">
                {providerKeys.map((apiKey) => (
                  <Card
                    key={apiKey.id}
                    variant="glass"
                    className="p-4"
                    data-testid={`llm-key-row-${apiKey.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center shrink-0">
                          <Key className="w-5 h-5 text-indigo-400" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h5 className="font-medium text-gray-200 truncate">
                              {apiKey.name}
                            </h5>
                            {apiKey.is_default && (
                              <span
                                className="inline-flex items-center gap-1 text-xs text-amber-300"
                                aria-label={t('settings.llmKeys.default')}
                              >
                                <Star className="w-3.5 h-3.5 fill-amber-300" />
                                {t('settings.llmKeys.default')}
                              </span>
                            )}
                            <ApiKeyHealthBadge
                              lastTestSuccess={apiKey.last_test_success}
                              lastTestedAt={apiKey.last_tested_at}
                            />
                          </div>
                          <div className="mt-1 text-xs text-gray-500 font-mono">
                            {apiKey.key_preview}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {!apiKey.is_default && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSetDefault(apiKey)}
                          >
                            {t('settings.llmKeys.setDefault')}
                          </Button>
                        )}
                        <TestKeyButton keyId={apiKey.id} />
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={t('settings.llmKeys.delete')}
                          className="text-gray-500 hover:text-red-400"
                          onClick={() => setDeleteTarget(apiKey)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <CreateLlmKeyDialog open={createOpen} onOpenChange={setCreateOpen} />

      <ConfirmationDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
        title={t('settings.llmKeys.deleteTitle')}
        description={
          deleteTarget ? (
            <>
              {t('settings.llmKeys.deleteDescription')}
              <span className="block mt-2 font-mono text-gray-300">
                {deleteTarget.name}
              </span>
            </>
          ) : (
            ''
          )
        }
        confirmLabel={t('settings.llmKeys.deleteConfirm')}
        confirmingLabel={t('settings.llmKeys.deleteConfirming')}
        confirmVariant="action-red"
        isProcessing={isDeleting}
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}
